import { useEffect, useState } from "react";
import { getAutonomousDecisionData } from "../../../api/autonomousDecisionService";

function AutonomousDecisionEnginePage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);

            try {
                const result = await getAutonomousDecisionData();
                setData(result);
            } catch (err) {
                console.error("Autonomous Decision Engine load failed", err);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    if (loading) {
        return <div style={{ padding: 20 }}>Loading Autonomous Decision Engine...</div>;
    }

    if (!data) {
        return <div style={{ padding: 20 }}>Decision data not available.</div>;
    }

    const decisionStatus = getDecisionStatus(data);

    return (
        <div style={{ padding: 20 }}>
            <h2>Autonomous Decision Engine</h2>

            <p style={{ color: "#666" }}>
                Intelligent decision support module combining AI, Trust Score, Blockchain, IoT telemetry,
                security events and logistics status.
            </p>

            <div style={gridStyle}>
                <Card title="Trust Score" value={`${data.trustScore}%`} color="#2E7D32" />
                <Card title="AI Risk" value={`${data.aiRisk}%`} color={data.aiRisk > 25 ? "#C62828" : "#2E7D32"} />
                <Card title="ETA Accuracy" value={`${data.etaAccuracy}%`} color="#1976D2" />
                <Card title="Decision Confidence" value={`${data.confidence}%`} color="#6A1B9A" />
                <Card title="Shipments" value={data.shipments?.length ?? 0} color="#3949AB" />
                <Card title="IoT Devices" value={data.devices?.length ?? 0} color="#00897B" />
                <Card title="Blockchain Events" value={data.blockchain?.length ?? 0} color="#546E7A" />
                <Card title="System Logs" value={data.logs?.length ?? 0} color="#EF6C00" />
            </div>

            <div style={mainPanelStyle}>
                <h3>Decision Status</h3>

                <div
                    style={{
                        ...statusBoxStyle,
                        borderLeft: `8px solid ${decisionStatus.color}`,
                    }}
                >
                    <h2 style={{ margin: 0, color: decisionStatus.color }}>
                        {decisionStatus.label}
                    </h2>

                    <p style={{ marginBottom: 0 }}>
                        {decisionStatus.description}
                    </p>
                </div>
            </div>

            <div style={twoColumnStyle}>
                <Panel title="AI Recommendation">
                    <p style={{ fontSize: 18 }}>
                        🤖 {data.recommendation || "No recommendation available."}
                    </p>
                </Panel>

                <Panel title="Autonomous Action">
                    <p style={{ fontSize: 18 }}>
                        ⚙️ {data.action || "No automatic action required."}
                    </p>
                </Panel>
            </div>

            <div style={twoColumnStyle}>
                <Panel title="Decision Inputs">
                    <Info label="AI Risk" value={`${data.aiRisk}%`} />
                    <Info label="Trust Score" value={`${data.trustScore}%`} />
                    <Info label="ETA Accuracy" value={`${data.etaAccuracy}%`} />
                    <Info label="Confidence" value={`${data.confidence}%`} />
                    <Info label="Blockchain Records" value={data.blockchain?.length ?? 0} />
                    <Info label="IoT Devices" value={data.devices?.length ?? 0} />
                    <Info label="System Logs" value={data.logs?.length ?? 0} />
                </Panel>

                <Panel title="Suggested Operational Rules">
                    <Rule text="If AI Risk is above 30%, employee review is required." />
                    <Rule text="If Trust Score falls below 70%, shipment should be flagged." />
                    <Rule text="If IoT device is offline, tracking reliability should be reduced." />
                    <Rule text="If blockchain integrity fails, shipment audit must be locked." />
                    <Rule text="If ETA accuracy falls below 80%, route recalculation should be suggested." />
                </Panel>
            </div>

            <div style={mainPanelStyle}>
                <h3>Latest System Signals</h3>

                {data.logs?.length > 0 ? (
                    <div style={{ display: "grid", gap: 10 }}>
                        {data.logs.slice(0, 8).map((log, index) => (
                            <div key={log.id || index} style={eventStyle}>
                                <b>{log.eventType || log.type || "System Event"}</b>
                                <br />
                                {log.message || log.description || "No message"}
                                <br />
                                <span style={{ color: "#777", fontSize: 12 }}>
                                    {log.createdAt || log.timestamp || "—"}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p>No system signals found.</p>
                )}
            </div>
        </div>
    );
}

function getDecisionStatus(data) {
    const risk = Number(data.aiRisk ?? 0);
    const trust = Number(data.trustScore ?? 0);
    const confidence = Number(data.confidence ?? 0);

    if (risk >= 50 || trust < 60) {
        return {
            label: "Critical Decision Required",
            color: "#C62828",
            description:
                "The system detected a high-risk condition. Manual intervention and operational review are recommended.",
        };
    }

    if (risk >= 25 || trust < 75 || confidence < 80) {
        return {
            label: "Review Recommended",
            color: "#EF6C00",
            description:
                "The logistics situation is stable but requires employee review due to moderate risk or reduced confidence.",
        };
    }

    return {
        label: "Autonomous Operation Stable",
        color: "#2E7D32",
        description:
            "The system can continue normal operations without automatic intervention.",
    };
}

function Card({ title, value, color }) {
    return (
        <div style={{ ...cardStyle, borderTop: `6px solid ${color}` }}>
            <div style={{ color: "#666", fontSize: 14 }}>{title}</div>
            <div style={{ fontSize: 30, fontWeight: "bold", marginTop: 10 }}>
                {value}
            </div>
        </div>
    );
}

function Panel({ title, children }) {
    return (
        <div style={panelStyle}>
            <h3>{title}</h3>
            {children}
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div style={infoStyle}>
            <b>{label}:</b> {value}
        </div>
    );
}

function Rule({ text }) {
    return (
        <div style={ruleStyle}>
            ✅ {text}
        </div>
    );
}

const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginTop: 20,
};

const twoColumnStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 20,
    marginTop: 24,
};

const cardStyle = {
    background: "white",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
};

const mainPanelStyle = {
    marginTop: 24,
    background: "white",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
};

const panelStyle = {
    background: "white",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
};

const statusBoxStyle = {
    background: "#fafafa",
    borderRadius: 12,
    padding: 18,
};

const infoStyle = {
    borderBottom: "1px solid #eee",
    padding: "8px 0",
};

const ruleStyle = {
    borderBottom: "1px solid #eee",
    padding: "10px 0",
};

const eventStyle = {
    borderBottom: "1px solid #eee",
    paddingBottom: 8,
};

export default AutonomousDecisionEnginePage;